import * as native from '@/utils/native';
import {
  redEnvelopePromotionInfoAtom,
  resultModalTitleAtom,
  visibleResultAtom,
} from './../store/common';
import {
  rebateCodeAtom,
  identificationAtom,
  teamInfoAtom,
  vipInfoAtom,
  giftLevelAtom,
} from './../store/user';
import { subEntranceBizCodeAtom } from './../store/sub';
import {
  productsAtom,
  loadingAtom,
  qrCodeUrlAtom,
  curProductAtom,
  seatsAtom,
  activeIndexAtom,
  payProductInfoAtom,
} from './../store/product';
import * as Sentry from '@sentry/react';
import {
  isTeamSubscription,
  SubscribeType,
  subscribeType,
  getAppIdEnum,
  isFromLivePPT,
  isFromAiLogo,
  isFromPcHome,
  isFromCutout,
  purchaseScene,
  PurchaseScene,
} from '@/constants';
import {
  SubscribeEnv,
  Subscribe,
  SubscriptionPlatform,
  MeiDou,
  ClientInfo,
  Product,
  AccountType,
  GroupEntranceScene,
  SubscribeGroupAPIPaths,
  TradePurchaseScene,
  ProductType,
  GroupProduct,
  SubscriptionPeriod,
} from '@meitu/subscribe-core';
import { getSkuType, getUrlParam, jsonToHump, showToast } from '.';
import { getDefaultStore } from 'jotai';
import { curTabAtom } from '@/store';
import { EntranceBizCodeType, PlatfromType, TabType } from '@/constants/subscription';
import {
  checkIsDefaultSecondTierMeidou,
  checkIsInCutoutExperimentalGroup,
  checkIsInMeidouExperimentalGroup,
  checkIsInPPTExperimentalGroup,
  checkIsInSubExperimentalGroup,
  checkIsInSubExperimentalGroupInfo,
  initAbTest,
} from './abtest';
import { getIncomeSource, getVipTag, isAppStorePay } from './vip';
import { getTrackSubscribeDataParams } from './transferId';
import { getMeidouCount, getUserInfo } from './user';
import { track } from './common';
import { fetchProductListOfMac } from '@/services/subscription';

const store = getDefaultStore();

let pollTimer: any;
let subInstance: Subscribe | MeiDou;
let transferId: string = '';
let isFirstSetActiveIndex = true;

const getSdkOptions = () => {
  // 美图大账号token
  const accessToken = window.localStorage.getItem('access_token') || '';

  let env: any;
  switch (process.env.REACT_APP_ENV) {
    case 'pre':
      env = SubscribeEnv.Pre;
      break;
    case 'beta':
      env = SubscribeEnv.Beta;
      break;
    default:
      env = SubscribeEnv.Release;
  }

  let appId = '';
  switch (subscribeType) {
    case SubscribeType.MEIDOUPACKAGE:
      appId = getAppIdEnum().MEIDOUPACKAGE;
      break;
    default:
      appId = getAppIdEnum().MEITUSHEJISHI;
  }

  const clientInfo: ClientInfo = {
    appId,
    sdkVersion: '1.0.0',
    platform: SubscriptionPlatform.PCWeb,
  };

  if (isTeamSubscription) {
    clientInfo.oid = getUrlParam('oid');
    clientInfo.groupId = getUrlParam('groupId');
  }
  return {
    accessToken,
    clientInfo,
    env,
  };
};

/**
 * 初始化会员SDK
 */
export const initSubscribeSdk = () => {
  const options = getSdkOptions();

  // 实例化web sdk
  const instance = new Subscribe(options);

  return instance;
};

/**
 * 初始化美豆SDK
 * @returns
 */
export const initMeidouSdk = () => {
  const options = getSdkOptions();

  // 实例化美豆 sdk
  const instance = new MeiDou(options);

  return instance;
};

/**
 * 美豆商品列表
 */
const getMeidouProducts = async () => {
  // 初始化SDK
  subInstance = initMeidouSdk();

  // 获取商品列表数据
  try {
    const { productList } = await (subInstance as MeiDou).fetchMeiDouEntrance({
      entranceBizCode: isTeamSubscription
        ? 'team-meiye'
        : checkIsInMeidouExperimentalGroup()
        ? 'designkit.meiye_test'
        : 'designkit.meiye',
    });
    store.set(productsAtom, productList || []);
  } catch (e) {
    console.log(e, 'fetch meidou entrance error=========');
  }
};

/**
 * 会员商品列表
 * @returns
 */
const getSubProducts = async () => {
  // 初始化SDK
  subInstance = initSubscribeSdk();

  // PC端区分批处理或者会员订阅标识
  const type = getUrlParam('type');
  const curTab = store.get(curTabAtom);
  const subEntranceBizCode = store.get(subEntranceBizCodeAtom);
  const rebateCode = store.get(rebateCodeAtom);
  const identification = store.get(identificationAtom);

  // 商品组入口标识
  let entranceBizCode = '';

  if (type === 'svip') {
    // 批处理
    entranceBizCode = 'mtxxpc.batch';
  } else if (curTab === TabType.CUTOUT) {
    // 购买张数
    entranceBizCode = 'sjs_cutout_dangou1';
  } else if (subscribeType === SubscribeType.MEIDOUPACKAGE) {
    // 商拍美豆套餐
    entranceBizCode = 'aicp_common';
  } else if (curTab === TabType.PRO_SUB) {
    const latestGiftLevel = store.get(giftLevelAtom);
    switch (latestGiftLevel) {
      case 1:
        entranceBizCode = 'sjs_Pro_TIER1';
        break;
      case 2:
        entranceBizCode = 'sjs_Pro_TIER2';
        break;
      case 3:
        entranceBizCode = 'sjs_Pro_TIER3';
        break;
      case 4:
        entranceBizCode = 'sjs_Pro_TIER4';
        break;
      default:
    }
  } else if (subscribeType === SubscribeType.LOGO_UNLIMITED) {
    // logo无限次
    entranceBizCode = 'sjsweb_logo_unlimited';
  } else {
    // 购买会员
    entranceBizCode = subEntranceBizCode || 'sjsweb_vip';

    // livePPT、logo、PC站点场景下的会员组入口标识
    if (
      isFromLivePPT ||
      isFromAiLogo ||
      isFromPcHome // PC站点
    ) {
      entranceBizCode = 'LivePPT_VIP';
    }

    // PC端下的会员入口标识
    if (getIncomeSource() !== 'MTXX-WEB') {
      entranceBizCode = subEntranceBizCode || 'xiuxiu_pro.vip_center_pc_test';
    }

    // web团队版会员订阅
    if (isTeamSubscription && getIncomeSource() === 'MTXX-WEB') {
      entranceBizCode = 'team-subscribe';
    }

    // web端，个人版，会员商品组，进入实验
    if (
      checkIsInSubExperimentalGroup() &&
      getIncomeSource() === 'MTXX-WEB' &&
      !isTeamSubscription &&
      curTab === TabType.SUB
    ) {
      entranceBizCode = 'designkit.svip.activity_normal';
    }

    // PPT/logo/抠图场景实验组1
    if (
      (((isFromAiLogo || isFromLivePPT) && checkIsInPPTExperimentalGroup().isHitGroupOne) ||
        (isFromCutout && checkIsInCutoutExperimentalGroup().isHitGroupOne)) &&
      curTab === TabType.SUB
    ) {
      entranceBizCode = 'sku_test';
    } else if (
      (((isFromAiLogo || isFromLivePPT) && checkIsInPPTExperimentalGroup().isHitGroupTwo) ||
        (isFromCutout && checkIsInCutoutExperimentalGroup().isHitGroupTwo)) &&
      curTab === TabType.SUB
    ) {
      entranceBizCode = 'sku_test1';
    }

    // web端个人版，邀请好友分佣页面打开的订阅，且是通过邀请链接进入的用户（即被打标记过的用户），商品组标识单独
    if (
      getIncomeSource() === 'MTXX-WEB' &&
      getUrlParam('isFromInvitePage') === '1' &&
      !isTeamSubscription &&
      curTab === TabType.SUB &&
      rebateCode &&
      entranceBizCode === 'sjsweb_vip' // 解决从渠道进入邀请兑换后，商品组展示不对问题（如果从渠道邀请兑换后服务端下发的entranceBizCode是对应是优惠商品组）
    ) {
      entranceBizCode = 'commission_discounts';
    }

    // web端，个人版，会员商品组，下架0.9实验
    if (checkIsInSubExperimentalGroupInfo().isHitGroupOne && curTab === TabType.SUB) {
      entranceBizCode = 'sjsweb_test1';
    } else if (checkIsInSubExperimentalGroupInfo().isHitGroupTwo && curTab === TabType.SUB) {
      entranceBizCode = 'sjsweb_test2';
    }
  }

  if (!subInstance || !entranceBizCode) return [];

  const params: any = {
    entranceBizCode,
  };

  if (entranceBizCode === 'designkit.svip.activity_normal' && identification) {
    params.userLayerCode = identification;
  }

  if (isTeamSubscription) {
    params.accountId = getUrlParam('groupId');
    params.accountType = AccountType.GroupId;
    params.entranceScene =
      purchaseScene === PurchaseScene.EXTRA ? GroupEntranceScene.Addons : GroupEntranceScene.Normal;
    params.appId =
      subscribeType === SubscribeType.MEIDOUPACKAGE
        ? getAppIdEnum().MEIDOUPACKAGE
        : getAppIdEnum().MEITUSHEJISHI;
  }

  const fetchGroupProducts = async () => {
    const res = await subInstance.request(
      `${process.env.REACT_APP_API_SUB_HOST}${SubscribeGroupAPIPaths.products}`,
      { params }
    );

    return res?.data || [];
  };

  try {
    const res = isTeamSubscription
      ? await fetchGroupProducts()
      : await subInstance.fetchEntrance(params);

    let list: Product[] = isTeamSubscription ? res : res.productList;

    const vipInfo = store.get(vipInfoAtom);
    if (getIncomeSource() === 'MTXX-WEB' && curTab === TabType.SUB && getVipTag(vipInfo).proVip) {
      list = list.filter((item) => item.productType === ProductType.RenewedSubscription);
    }

    store.set(productsAtom, list || []);

    !isTeamSubscription && store.set(redEnvelopePromotionInfoAtom, res.redEnvelopePromotion);
  } catch (e) {
    console.log(e, 'fetch sub entrance error=========');
  }
};

/**
 * 获取mac商店的商品列表
 */
export const getProductMacList = async () => {
  const subEntranceBizCode = store.get(subEntranceBizCodeAtom);
  const countryCode = await native.getCountryCode();
  const params = {
    app_id: getAppIdEnum().MEITUSHEJISHI,
    access_token: localStorage.getItem('access_token') || '',
    entrance_biz_code: subEntranceBizCode || EntranceBizCodeType.XIUXIUPRO,
    platform: PlatfromType.IOS,
    country_code: countryCode,
    expected_language: 'zh-Hans',
  };

  const res = await fetchProductListOfMac(params);

  if (res?.data?.code === 0 && res?.data?.data) {
    const list = res.data.data;

    jsonToHump(list);

    list.forEach((item: any) => {
      item.checkBox = {};
      if (item.buttonSubtitle) {
        item.checkBox.explain = item.buttonSubtitle;
      }
      item.appId = item.appIdString;
      item.productId = item.productIdString;
    });

    store.set(productsAtom, list as any);
  }
};

/**
 * 获取商品组列表数据
 */
export const getProducts = async () => {
  const curTab = store.get(curTabAtom);
  if (curTab === TabType.MEIDOU) {
    // 美豆商品
    await getMeidouProducts();
  } else {
    // 非美豆商品
    await getSubProducts();
  }
};

export const getTransferId = () => {
  return transferId;
};

/**
 * 轮询交易会话的支付结果
 * @param productInfo 轮询交易会话的支付结果
 */
const pollTradeProgress = (productInfo: Product) => {
  const curTab = store.get(curTabAtom);
  pollTimer = window.setInterval(async () => {
    try {
      const res = await subInstance
        ?.productTradeProgress({
          tradeSessionId: productInfo.tradeSessionId, // 需要轮询商品列表交易进度时，必传
        })
        .catch((error: any) => {
          // 黑灰产策略触发异常提示
          showToast(
            error?.message || error || '',
            document.querySelector('.contentBox') as HTMLDivElement
          );
          clearInterval(pollTimer);
          setTimeout(() => {
            store.set(activeIndexAtom, -1);
            getProducts();
          }, 3000);
        });

      if (res?.havePaidTrade) {
        // 等待2秒，确保用户权益信息已更新
        await new Promise((resolve) => setTimeout(resolve, 2000));
        await getUserInfo();
        store.set(resultModalTitleAtom, '感谢您的订阅！');
        store.set(visibleResultAtom, true);

        clearInterval(pollTimer);

        const orderPaid = res.transactionList.find((item) => item.payStatus === 1);

        // 普通会员是否配置买赠商品
        const promotionGiftForConsumable = orderPaid?.promotionGifts?.find(
          (item: any) => item.productType === ProductType.Consumable
        );
        store.set(payProductInfoAtom, orderPaid?.product || {});
        getMeidouCount();
        let params: any = {};
        if (getIncomeSource() === 'MTXX-WEB') {
          params = {
            class: orderPaid?.payChannel === 'weixin' ? 'wechat' : 'zfb',
            name_id: orderPaid?.product?.productId || '',
            money: (orderPaid as any)?.payAmount / 100, // 订单金额
            sku_type: getSkuType(),
            // 订单类型(1:非续期订阅 2:续期订阅 3:非消耗品 4:消耗品)
            order_type: orderPaid?.product?.productType || 0,
            touch_type: getUrlParam('touch_type') || 4,
            order_id: orderPaid?.transactionId, // 续期=合约号，非续期=订单号
          };

          // 团队版购买会员场景
          if (isTeamSubscription && curTab === TabType.SUB) {
            params['seat_num'] = (orderPaid?.product as any)?.quantity;
          }

          if ((orderPaid?.product?.productType as any) === 'CONSUMALBE') {
            // 买张数商品
            const commodityConfig = JSON.parse(orderPaid?.product.commodityConfig as string);
            params.buy_num = commodityConfig?.consumableCount?.count;
          } else if (promotionGiftForConsumable) {
            // 普通会员买赠商品
            params.buy_num = promotionGiftForConsumable?.commodityConfig?.count;
          }
        } else {
          params = {
            category: orderPaid?.payChannel === 'weixin' ? '微信' : '支付宝',
            product_id: orderPaid?.product?.productId || '',
            amount: ((orderPaid?.product?.price || 0) / 100).toString(),
          };
        }
        track({
          event_id: 'vip_paidprocess_success',
          event_name: '支付成功',
          params,
        });
      }
    } catch (e) {
      Sentry.captureException(
        `productTradeProgress error >>>>>>>>: ${typeof e === 'string' ? e : JSON.stringify(e)}`
      );
    }
  }, 5000);
};

// 创建交易二维码
const createQrCode = async (productInfo: Product, transfer_id: string) => {
  const curTab = store.get(curTabAtom);
  const seats = store.get(seatsAtom);
  try {
    // 取第一个促销优惠
    const promotionInfo = productInfo?.promotions && productInfo.promotions[0];
    const params: any = {
      productId: productInfo.productId,
      transferId: transfer_id, // 透传 ID
      promotionId: promotionInfo?.promotionId || '', // 促销优惠时，必传
      tradeSessionId: productInfo.tradeSessionId, // 需要轮询商品列表交易进度时，必传
    };

    if (isTeamSubscription) {
      params.quantity = curTab === TabType.SUB ? seats : 0;
      params.purchaseScene =
        purchaseScene === PurchaseScene.EXTRA
          ? TradePurchaseScene.Extra
          : TradePurchaseScene.Normal;
    }

    const res = await subInstance?.createTradeQrCode(params);

    store.set(qrCodeUrlAtom, res?.url || '');

    pollTradeProgress(productInfo);

    store.set(loadingAtom, false);
  } catch (e: any) {
    store.set(loadingAtom, false);
    // 上报非未登录之外的异常错误
    const msg = typeof e === 'string' ? e : e?.message;
    if (msg !== 'need login')
      Sentry.captureException(
        `createTradeQrCode error >>>>>>>>> ${getIncomeSource()} : ${
          typeof e === 'string' ? e : JSON.stringify(e)
        }`
      );
  }
};

/**
 * 创建透传数据
 * @param productInfo
 */
export const trackSubscribeData = async () => {
  const productInfo = store.get(curProductAtom);
  store.set(loadingAtom, true);
  store.set(qrCodeUrlAtom, '');
  try {
    const data = await getTrackSubscribeDataParams();

    const res = await subInstance.trackSubscribeData(data);

    if (res?.transferId) {
      transferId = res.transferId;
      createQrCode(productInfo, res.transferId);
    }
  } catch (e) {
    createQrCode(productInfo, '');
    Sentry.captureException(`trackSubscribeData error >>>>>>: ${e}`);
  }
};

/**
 * 清楚轮询的定时器
 */
export const clearPollTimer = () => {
  pollTimer && clearInterval(pollTimer);
};

/**
 * 刷新二维码
 */
export const handleRefreshQrCode = () => {
  // 清除上一次定时器
  clearPollTimer();

  const curProduct = store.get(curProductAtom);

  if (!curProduct) return;

  trackSubscribeData();
};

/**
 * 默认选中商品卡片
 */
export const initActiveProductIndex = () => {
  const products = store.get(productsAtom);
  const teamInfo = store.get(teamInfoAtom);
  const curTab = store.get(curTabAtom);
  const vipInfo = store.get(vipInfoAtom);

  let idx = products.findIndex((item) => item.preferred);
  idx = idx > -1 ? idx : products.length > 1 ? 1 : 0;

  if (curTab === TabType.SUB && getUrlParam('preferred') === 'month') {
    const monthIdx = products.findIndex(
      (item) =>
        item.productType === ProductType.RenewedSubscription &&
        item.subPeriod === SubscriptionPeriod.PerMonth
    );
    if (monthIdx > -1) idx = monthIdx;
  } else if (curTab === TabType.SUB && getUrlParam('preferred') === 'year') {
    const yearIdx = products.findIndex(
      (item) =>
        item.productType === ProductType.RenewedSubscription &&
        item.subPeriod === SubscriptionPeriod.PerYear
    );
    if (yearIdx > -1) idx = yearIdx;
  }

  // 高级会员场景，仅首次加载时根据当前会员周期匹配默认选中商品
  if (curTab === TabType.PRO_SUB && vipInfo?.is_vip && isFirstSetActiveIndex) {
    const subPeriod = [SubscriptionPeriod.PerMonth, SubscriptionPeriod.PerQuarter].includes(
      vipInfo?.sub_period as SubscriptionPeriod
    )
      ? SubscriptionPeriod.PerMonth
      : vipInfo?.sub_period === SubscriptionPeriod.PerYear
      ? SubscriptionPeriod.PerYear
      : vipInfo.sub_period;
    const defaultIdx = products.findIndex(
      (item) => item.productType === ProductType.RenewedSubscription && item.subPeriod === subPeriod
    );
    if (defaultIdx > -1) {
      idx = defaultIdx;
    }
  }

  // 如果URL指定product_id，则选中对应的商品
  const productId = getUrlParam('product_id');
  if (productId) {
    idx = products.findIndex((item) => item.productId === productId);
  }

  store.set(activeIndexAtom, idx);

  isFirstSetActiveIndex = false;

  // 设置默认席位数为商品的最小席位数，团队版会员订阅
  if (isTeamSubscription && curTab === TabType.SUB) {
    if (purchaseScene === PurchaseScene.EXTRA) {
      // 加购场景，默认1
      store.set(seatsAtom, 1);
    } else {
      const current = products[idx] as GroupProduct;
      const lowerLimit = current?.seatInfo?.lowerLimit;
      // 当前团队绑定的成员数
      const userCount = teamInfo?.member_detail?.user_count || 0;
      const defaultSeats = +getUrlParam('seats');
      lowerLimit &&
        store.set(
          seatsAtom,
          current.isProbation ? lowerLimit : Math.max(lowerLimit, userCount, defaultSeats)
        );
    }
  }
};

export const initFn = async (callback?: () => void) => {
  isFirstSetActiveIndex = true;
  try {
    if (!isAppStorePay()) {
      // web端，非团队版
      if (!isTeamSubscription && getIncomeSource() === 'MTXX-WEB') {
        await initAbTest();
        // 高级会员 tab 下根据实验决定美豆默认第一档还是第二档（需在 initAbTest 之后执行）
        if (store.get(curTabAtom) === TabType.PRO_SUB && getUrlParam('isOnlyShowScanUI') !== '1') {
          store.set(giftLevelAtom, checkIsDefaultSecondTierMeidou() ? 2 : 1);
        }
      }

      getProducts();
      // 获取美豆剩余详细
      if ([SubscribeType.MEIDOU, SubscribeType.MEIDOUPACKAGE].includes(subscribeType)) {
        getMeidouCount();
      }
    } else {
      getProductMacList();
    }

    callback?.();
  } catch (e) {
    Sentry.captureException(`init error >>>>>>: ${e}`);
  }
};
